import type {
  QuoteBackgroundFrame, QuoteCarousel, QuoteSlideStyle, QuoteTextPosition,
} from './types';
import { SLIDE_FORMATS } from './slideFormats';
import { resolveSlideWatermark } from './resolveSlideWatermark';
import { resolveQuoteTextStyle, type QuoteSlideTypography } from './quoteTextStyle';

export {
  defaultQuoteTypography, FRAME_OUTLINE_WIDTH_PCT, type QuoteSlideTypography, type QuoteTextAlign,
} from './quoteTextStyle';

export interface QuoteSlideBrand {
  /** CSS family or backend font id. */
  fontFamily: string;
  /** Persisted font file URL when custom. */
  fontUrl: string | null;
  /** #RRGGBB */
  textColor: string;
  /** #RRGGBB */
  accentColor: string;
  /** #RRGGBB */
  backgroundColor: string;
}

export const DEFAULT_QUOTE_BRAND: QuoteSlideBrand = {
  fontFamily: 'Roboto',
  fontUrl: null,
  textColor: '#ffffff',
  accentColor: '#c6ff3d',
  backgroundColor: '#111111',
};

export interface QuoteSlideProps extends Record<string, unknown> {
  style: QuoteSlideStyle;
  width: number;
  height: number;
  text: string;
  handle: string;
  /** Frame image for style 'frame' or a slide-level frame override. */
  backgroundUrl: string | null;
  backgroundColor: string;
  brand: QuoteSlideBrand;
  watermark: boolean;
  /** 0-based. */
  slideIndex: number;
  slideCount: number;
  /** Absent = `defaultQuoteTypography(style)`. */
  typography?: QuoteSlideTypography;
  /** Center of the text block (fractions); absent/null = style default. */
  textPosition?: QuoteTextPosition | null;
  /** Pan/zoom of the background image; absent/null = cover, centered. */
  backgroundFrame?: QuoteBackgroundFrame | null;
}

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

function validHexColor(value: unknown): string | null {
  return typeof value === 'string' && HEX_COLOR_RE.test(value) ? value.toLowerCase() : null;
}

interface BrandKitLike {
  data?: {
    colors?: unknown;
    title_font?: { font_id?: string | null } | null;
    subtitle?: { subtitle_font_url?: string | null } | null;
  };
}

/**
 * Maps a brand kit (as returned by `myBrandKit`/`myBrandKits`) onto the
 * quote-slide brand shape. `colors` is the kit's flat hex-string array:
 * [0] background, [1] text, [2] accent — each validated and falling back to
 * `DEFAULT_QUOTE_BRAND` independently. Font comes from `title_font.font_id`;
 * `fontUrl` from `subtitle.subtitle_font_url` when present (older/real kit
 * payloads may not carry this field, in which case it stays null).
 */
export function brandFromKit(kit: BrandKitLike | null): QuoteSlideBrand {
  const colors = Array.isArray(kit?.data?.colors) ? (kit!.data!.colors as unknown[]) : [];
  const fontId = kit?.data?.title_font?.font_id;
  const fontUrl = kit?.data?.subtitle?.subtitle_font_url;

  return {
    fontFamily: fontId || DEFAULT_QUOTE_BRAND.fontFamily,
    fontUrl: fontUrl ?? null,
    backgroundColor: validHexColor(colors[0]) ?? DEFAULT_QUOTE_BRAND.backgroundColor,
    textColor: validHexColor(colors[1]) ?? DEFAULT_QUOTE_BRAND.textColor,
    accentColor: validHexColor(colors[2]) ?? DEFAULT_QUOTE_BRAND.accentColor,
  };
}

export function buildQuoteSlideProps(args: {
  carousel: QuoteCarousel;
  slideIndex: number;
  brand: QuoteSlideBrand;
  isPro: boolean;
}): QuoteSlideProps {
  const { carousel, slideIndex, isPro } = args;
  // Template brand → carousel text style overrides.
  const { brand, typography } = resolveQuoteTextStyle({
    brand: args.brand, style: carousel.style, textStyle: carousel.textStyle,
  });
  const slide = carousel.slides[slideIndex];
  const { width, height } = SLIDE_FORMATS[carousel.format];
  const background = slide?.background ?? null;

  return {
    style: carousel.style,
    width,
    height,
    text: slide?.text ?? '',
    handle: carousel.handle,
    backgroundUrl: background?.kind === 'frame' ? background.url : null,
    backgroundColor: background?.kind === 'color' ? background.color : brand.backgroundColor,
    brand,
    watermark: resolveSlideWatermark({ isPro }),
    slideIndex,
    slideCount: carousel.slides.length,
    typography,
    textPosition: slide?.textPosition ?? null,
    backgroundFrame: slide?.backgroundFrame ?? null,
  };
}
